'use client';

// npm imports
import { useCallback, useEffect, useRef, useState } from 'react';
import type { formStatusData, privateFunctionsFormData } from '@lib/types';
import TextInput from '@components/Forms/FormComponents/TextInput/textInput';
import DateInput from '@components/Forms/FormComponents/DateInput/dateinput';
import NumInput from '@components/Forms/FormComponents/NumInput/numInput';
import TextAreaInput from '@components/Forms/FormComponents/TextAreaInput/textAreaInput';
import Grid from '@components/SectionComponents/Grid';
import buttonStyles from '@components/SectionComponents/Buttons/button.module.scss';
import FormStatus from '@components/Forms/FormComponents/FormStatus/formStatus';
import LoadingSpinner from '@components/Forms/FormComponents/LoadingSpinner/loadingSpinner';

import formStyles from '@components/Forms/FormComponents/formStyles.module.scss';
import { executeRecaptcha } from '@components/Forms/FormComponents/RecaptchaFormComponent/RecaptchaClientFunctions';
import GTMEvent from '@lib/gtm_events/GTMEvent';

const FORM_CONFIG = {
    instructionsId: 'form-instructions',
    initialState: {
        name: '',
        email: '',
        phone: '',
        preferredDate: '',
        numGuests: '',
        message: '',
        title: '',
    },
    btnDisable: false,
};

const PrivateFunctionsForm = () => {
    const [formObject, setFormObject] = useState(FORM_CONFIG.initialState);
    const [btnDisable, setBtnDisable] = useState(FORM_CONFIG.btnDisable);

    const topOfFormRef = useRef<HTMLFormElement>(null);
    const [formStatusData, setFormStatusData] = useState<formStatusData>({
        message: '',
        show: false,
        colour: 'red',
    });

    const handleReset = useCallback(() => {
        setFormObject(FORM_CONFIG.initialState);
        setBtnDisable(FORM_CONFIG.btnDisable);
        scrollToTopOfFormRef();
    }, []);

    const apiHandler = async () => {
        const token = await executeRecaptcha();

        const privateFunctionsFormData: privateFunctionsFormData = {
            name: formObject.name,
            email: formObject.email,
            phone: formObject.phone,
            preferredDate: formObject.preferredDate,
            numGuests: formObject.numGuests,
            message: formObject.message,
            recaptchaToken: token,
            title: formObject.title,
        };

        // https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch
        const response = await fetch('/api/privateFunctionsForm', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(privateFunctionsFormData),
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'An unknown error occurred');
        }
    };

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        setBtnDisable(true);

        if (
            !formObject.name ||
            !formObject.email ||
            !formObject.phone ||
            !formObject.preferredDate ||
            !formObject.numGuests ||
            !formObject.message
        ) {
            setFormStatusData({
                message:
                    'Please complete all the required fields before submitting',
                show: true,
                colour: 'orange',
            });
            scrollToTopOfFormRef();
            setBtnDisable(false);
            return;
        }

        try {
            await apiHandler();

            GTMEvent('form_submit', { form_name: 'private functions' });

            setFormStatusData({
                message:
                    'Thank you for contacting us, we will be in touch shortly',
                show: true,
                colour: 'green',
            });
            handleReset();
            setBtnDisable(false);
        } catch (e) {
            const err = e as Error;
            setFormStatusData({
                message: `Crikey! Something went wrong: ${err.message}`,
                show: true,
                colour: 'red',
            });
            setBtnDisable(false);
        }
    };

    const handleInvalid = () => {
        if (!topOfFormRef.current) return;

        setTimeout(() => {
            scrollToTopOfFormRef();
        });

        setFormStatusData({
            message:
                'Please complete all the required fields before submitting',
            show: true,
            colour: 'orange',
        });
    };

    const scrollToTopOfFormRef = useCallback(() => {
        const target = document.getElementById('privateFunction-form');
        target?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    }, []);

    useEffect(() => {
        window.addEventListener(
            'privateFunction-form-scroll',
            scrollToTopOfFormRef,
        );
        return () =>
            window.removeEventListener(
                'privateFunction-form-scroll',
                scrollToTopOfFormRef,
            );
    }, [scrollToTopOfFormRef]);

    return (
        <>
            <form
                id="contact-form"
                ref={topOfFormRef}
                onSubmit={handleSubmit}
                onInvalid={handleInvalid}
                style={{ width: '100%', maxWidth: '1170px' }}
                className={formStyles.scroll}
            >
                {btnDisable && (
                    <div role="status" aria-live="polite">
                        <LoadingSpinner active={btnDisable} />
                    </div>
                )}

                {formStatusData.show && (
                    <div aria-live="polite">
                        {/* Show any error or success messages */}
                        <FormStatus
                            message={formStatusData.message}
                            colour={formStatusData.colour}
                        />
                    </div>
                )}

                <p
                    id={FORM_CONFIG.instructionsId}
                    className={formStyles.formInstructions}
                >
                    <i>All fields are required.&nbsp;</i>
                    <span className={formStyles.asterisk}>*</span>
                </p>

                {/* Personal Details */}

                <TextInput
                    value={formObject.name}
                    onChange={(value) =>
                        setFormObject((prev) => ({ ...prev, name: value }))
                    }
                    id="fname"
                    name="fname"
                    title="Full Name"
                    placeholder="Enter your full name"
                    autoComplete="name"
                    required={true}
                    showRequired={false}
                    formInstructionsId={FORM_CONFIG.instructionsId}
                />
                {/** Honeypot field to try catch spam bots */}
                <TextInput
                    value={formObject.title}
                    onChange={(value) =>
                        setFormObject((prev) => ({ ...prev, title: value }))
                    }
                    id="title"
                    name="title"
                    title="Title"
                    placeholder="Enter your title"
                    autoComplete="off"
                    hideOffScreen={true}
                    tabIndex={-1}
                />
                <TextInput
                    value={formObject.email}
                    onChange={(value) =>
                        setFormObject((prev) => ({ ...prev, email: value }))
                    }
                    id="email"
                    name="email"
                    title="Email"
                    type="email"
                    placeholder="Enter your email address"
                    autoComplete="email"
                    required={true}
                    showRequired={false}
                    formInstructionsId={FORM_CONFIG.instructionsId}
                />
                <TextInput
                    value={formObject.phone}
                    onChange={(value) => {
                        const filtered = value.replace(/[^0-9()+\s]/g, '');
                        setFormObject((prev) => ({ ...prev, phone: filtered }));
                    }}
                    id="phone"
                    name="phone"
                    title="Phone Number"
                    type="tel"
                    placeholder="(+61) 432 123 321"
                    autoComplete="tel"
                    required={true}
                    showRequired={false}
                    formInstructionsId={FORM_CONFIG.instructionsId}
                />

                {/* Private Function Details */}
                <Grid columns={2}>
                    <DateInput
                        value={formObject.preferredDate}
                        onChange={(value) =>
                            setFormObject((prev) => ({
                                ...prev,
                                preferredDate: value,
                            }))
                        }
                        id="prefereddate"
                        name="prefereddate"
                        title="Preferred Date"
                        required={true}
                        showRequired={false}
                        formInstructionsId={FORM_CONFIG.instructionsId}
                    />

                    <NumInput
                        value={formObject.numGuests}
                        onChange={(value) =>
                            setFormObject((prev) => ({
                                ...prev,
                                numGuests: value,
                            }))
                        }
                        id="numguests"
                        name="numguests"
                        title="Number of Guests"
                        max={1000}
                        required={true}
                        showRequired={false}
                        formInstructionsId={FORM_CONFIG.instructionsId}
                    />
                </Grid>

                <TextAreaInput
                    value={formObject.message}
                    onChange={(value) =>
                        setFormObject((prev) => ({ ...prev, message: value }))
                    }
                    id="message"
                    name="message"
                    title="Your Message"
                    placeholder="Your Message"
                    required={true}
                    showRequired={false}
                    formInstructionsId={FORM_CONFIG.instructionsId}
                    maxLength={5000}
                />

                <br />

                <button
                    id="submit"
                    type="submit"
                    className={`${buttonStyles.btn}`}
                    disabled={btnDisable}
                >
                    <span id="button-text">SUBMIT</span>
                </button>
            </form>
        </>
    );
};

export default PrivateFunctionsForm;
